import { ApiError, post, request } from "./api";

/** A product's connections (node C1): Meta and the cold email provider. Results are read from Meta. */
export type ConnectionKind = "meta" | "email";
export type ConnectionState = "none" | "checking" | "ok" | "fix" | "warming";

/** One named check Flashadz ran: what it looked at, whether it passed, and the plain reason when not. */
export interface ConnectionCheck {
  id: string;
  label: string;
  ok: boolean;
  detail?: string;
  /** Shown, but never holds the connection back. */
  optional?: boolean;
  checkedAt: string;
}

/** A connection as GET /products/:id/connections returns it. Keys never come back, only their hint. */
export interface Connection {
  id?: string;
  kind: ConnectionKind;
  state: ConnectionState;
  config: Record<string, unknown>;
  secretHint: string | null;
  checks: ConnectionCheck[];
  updatedAt?: string;
}

export interface MetaAdAccount {
  id: string;
  name: string | null;
  currency: string | null;
  timezone: string | null;
  usable: boolean;
  why: string | null;
}

/** A pixel the ad account can reach, as Meta last listed it. */
export interface MetaPixel {
  id: string;
  name: string | null;
  status: string | null;
}

/** A sending mailbox in the owner's cold email account. */
export interface Mailbox {
  id: number;
  fromEmail: string;
  fromName: string | null;
  maxPerDay: number | null;
  smtpOk: boolean;
  imapOk: boolean;
  warmup: { status: string | null; reputation: number | null; blocked: boolean; blockedReason: string | null };
}

export const metaOf = (c: Connection | undefined) => {
  const k = (c?.config ?? {}) as {
    accountId?: string;
    adAccountId?: string;
    adAccounts?: MetaAdAccount[];
    pixelId?: string;
    pixelName?: string | null;
    pixels?: MetaPixel[];
    lastError?: string | null;
  };
  return {
    signedIn: !!k.accountId,
    adAccountId: k.adAccountId ?? null,
    adAccounts: k.adAccounts ?? [],
    pixelId: k.pixelId ?? null,
    pixelName: k.pixelName ?? null,
    pixels: k.pixels ?? [],
    lastError: k.lastError ?? null,
  };
};

export const emailOf = (c: Connection | undefined) => {
  const k = (c?.config ?? {}) as { mailboxIds?: number[] };
  return { mailboxIds: Array.isArray(k.mailboxIds) ? k.mailboxIds : [] };
};

/** The connection's state; a product with no row for this kind is not connected. */
export const shownState = (c: Connection | undefined): ConnectionState => c?.state ?? "none";

/** Ready for a goal to use: every check that counts passes (a mailbox still warming up counts). */
export const connectionReady = (c: Connection | undefined) => ["ok", "warming"].includes(shownState(c));

const base = (productId: string) => `/products/${encodeURIComponent(productId)}/connections`;
const put = <T>(path: string, body: unknown) => request<T>(path, { method: "PUT", body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: "DELETE" });

/** What a check or a change answers: the connection's state and checks after it. */
export interface Checked {
  connection: { state: ConnectionState; checks: ConnectionCheck[] };
}

export const connectionsApi = {
  list: (productId: string) => request<{ connections: Connection[] }>(base(productId)),
  meta: {
    /** The address of Meta's sign-in, or null when the product is already connected. */
    start: (productId: string) => post<{ authUrl: string | null }>(`${base(productId)}/meta/start`),
    check: (productId: string) => post<Checked>(`${base(productId)}/meta/check`),
    adAccount: (productId: string, adAccountId: string) => put<Checked>(`${base(productId)}/meta/ad-account`, { adAccountId }),
    pixel: (productId: string, pixelId: string) => put<Checked>(`${base(productId)}/meta/pixel`, { pixelId }),
    disconnect: (productId: string) => del<unknown>(`${base(productId)}/meta`),
  },
  email: {
    connect: (productId: string, apiKey: string) => put<Checked & { mailboxes: Mailbox[] }>(`${base(productId)}/email`, { apiKey }),
    /** The mailboxes in the owner's Smartlead account, read with the stored key. */
    list: (productId: string) => request<{ mailboxes: Mailbox[] }>(`${base(productId)}/email/mailboxes`),
    mailboxes: (productId: string, ids: number[]) => put<Checked & { mailboxes: Mailbox[] }>(`${base(productId)}/email/mailboxes`, { ids }),
    check: (productId: string) => post<Checked>(`${base(productId)}/email/check`),
    disconnect: (productId: string) => del<unknown>(`${base(productId)}/email`),
  },
};

/** The checks that hold a connection back: failing and not optional. */
export const failing = (checks: ConnectionCheck[]) => checks.filter((c) => !c.ok && !c.optional);

/** The answer a write gets when something else changed the connection first: reload and show the latest. */
export const isChanged = (message: string) => /changed while Flashadz was working on it/i.test(message);

/**
 * Text from the server as the owner may see it. Why: the service behind the Meta connection is never named
 * to the owner; the API keeps its name out, and this guard catches any text stored before it did.
 */
export const hideProvider = (text: string) => text.replace(/\s*\([^)]*zernio[^)]*\)/gi, "").replace(/zernio/gi, "the Meta connection");

/** A failed request in words the owner can act on. Why: an unexpected failure only says "Internal server error". */
export function shownError(e: unknown): string {
  if (!(e instanceof ApiError)) return "Something went wrong. Try again.";
  if (e.status >= 500 && /^internal server error$/i.test(e.message)) return "Flashadz could not finish that just now. Try again in a minute.";
  return hideProvider(e.message);
}
